'use client'

import { useEffect, useState } from 'react'
import { apiFetch, readApiJson } from '@/lib/api-fetch'

interface MediaEdgeSettingsResponse {
  environment: 'local' | 'preview' | 'production'
  settings: { publicBaseUrl: string }
}

const EMPTY_SETTINGS: MediaEdgeSettingsResponse = {
  environment: 'local',
  settings: { publicBaseUrl: '' },
}

export function MediaEdgeSettings() {
  const [data, setData] = useState<MediaEdgeSettingsResponse>(EMPTY_SETTINGS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    let cancelled = false
    apiFetch('/api/settings/media-edge')
      .then(readApiJson<MediaEdgeSettingsResponse>)
      .then((response) => { if (!cancelled) setData(response) })
      .catch((reason: unknown) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : 'Не удалось загрузить адрес edge.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  function update(publicBaseUrl: string) {
    setData((current) => ({ ...current, settings: { publicBaseUrl } }))
    setSaved(false)
  }

  async function save() {
    if (saving) return
    setSaving(true)
    setError('')
    setSaved(false)
    try {
      const response = await apiFetch('/api/settings/media-edge', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data.settings),
      })
      const updated = await readApiJson<MediaEdgeSettingsResponse>(response)
      setData(updated)
      setSaved(true)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось сохранить адрес edge.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="mb-12">
      <div className="mb-5">
        <h2 className="text-[18px] font-semibold tracking-tight text-ink">Фотографии</h2>
        <div className="mt-0.5 text-[13px] text-ink-muted">Откуда браузер получает изображения изделий</div>
      </div>
      <div className="surface-card rounded-md p-4 sm:p-5">
        <label className="block text-[12px] font-semibold text-ink">
          Адрес edge для фотографий
          <input
            type="url"
            inputMode="url"
            autoComplete="url"
            className="input mt-2 w-full"
            placeholder="https://media.example.com"
            value={data.settings.publicBaseUrl}
            onChange={(event) => update(event.target.value)}
            disabled={loading || saving}
          />
        </label>
        <p className="mt-2 max-w-2xl text-[11px] leading-relaxed text-ink-faint">
          Укажите только адрес домена, например https://media.example.com. Пустое поле снимает сохранённый адрес: тогда используется FORGE_MEDIA_PUBLIC_BASE_URL из развертывания, а если и он не задан — фотографии отдаёт API. Изменение применяется к следующему ответу без перезапуска.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-rule pt-5">
          <button type="button" onClick={() => void save()} disabled={loading || saving} className="btn btn-primary">
            {saving ? 'Сохраняем…' : 'Сохранить адрес'}
          </button>
          {saved && <span role="status" className="text-[12px] text-st-delivered">Сохранено</span>}
          {error && <span role="alert" className="text-[12px] text-st-unpaid">{error}</span>}
        </div>
      </div>
    </section>
  )
}
